import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Minus, X, Send, FileText, Package, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function RivenditoreAcquista() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterAvail, setFilterAvail] = useState("all"); // all | pronto | importazione
  const [cart, setCart] = useState({}); // {sku_variante: qty}
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("catalog");

  const load = async () => {
    const [s, o] = await Promise.all([api.get("/b2b/casa-madre-stock"), api.get("/b2b/orders")]);
    setItems(s.data);
    setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.categoria))).filter(Boolean).sort(), [items]);
  const filtered = useMemo(() => {
    let arr = items;
    if (q) {
      const ql = q.toLowerCase();
      arr = arr.filter((i) => [i.nome, i.sku_variante, i.sku_madre, ...Object.values(i.combination || {})]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));
    }
    if (filterCat !== "all") arr = arr.filter((i) => i.categoria === filterCat);
    if (filterAvail === "pronto") arr = arr.filter((i) => i.stock_pronto > 0);
    if (filterAvail === "importazione") arr = arr.filter((i) => i.stock_pronto === 0 && i.stock_importazione > 0);
    return arr;
  }, [items, q, filterCat, filterAvail]);

  const addQty = (sku, max, delta) => {
    setCart((c) => {
      const cur = c[sku] || 0;
      const next = Math.max(0, Math.min(max, cur + delta));
      const copy = { ...c };
      if (next === 0) delete copy[sku]; else copy[sku] = next;
      return copy;
    });
  };

  const totaleCarrello = Object.entries(cart).reduce((s, [sku, qty]) => {
    const it = items.find((i) => i.sku_variante === sku);
    return s + (it ? it.prezzo_riv * qty : 0);
  }, 0);

  const submitPO = async () => {
    const list = Object.entries(cart).map(([sku, qty]) => ({ sku_variante: sku, quantita: qty }));
    if (list.length === 0) { toast.error("Carrello vuoto"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/b2b/orders", { items: list, note });
      toast.success(`PO ${r.data.numero_ordine} creato — € ${r.data.totale}`);
      setCart({}); setNote("");
      await load();
      setTab("orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione PO");
    } finally { setSubmitting(false); }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="riv-acquista-page">
        <div className="mb-6">
          <div className="overline text-brand mb-1">Rivenditore · B2B</div>
          <h1 className="font-heading font-bold text-3xl">Acquista da Casa Madre</h1>
          <p className="text-sm text-muted-foreground mt-1">Pagina di lavoro: solo combinazioni con stock disponibile in Casa Madre. Crea un PO veloce.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border mb-4">
          <button onClick={() => setTab("catalog")} data-testid="tab-catalog"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "catalog" ? "border-foreground" : "border-transparent text-muted-foreground"}`}>
            Catalogo CM ({items.length})
          </button>
          <button onClick={() => setTab("orders")} data-testid="tab-orders"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "orders" ? "border-foreground" : "border-transparent text-muted-foreground"}`}>
            I miei PO ({orders.length})
          </button>
        </div>

        {tab === "catalog" && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-4">
            <div>
              {/* Filters bar */}
              <div className="bg-white border border-border rounded-sm p-2 mb-2 flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="b2b-search"
                    placeholder="Cerca per SKU, nome, colore, piede..." className="w-full h-9 pl-9 pr-3 rounded-sm border border-border text-sm" />
                </div>
                <Select value={filterCat} onValueChange={setFilterCat}>
                  <SelectTrigger className="h-9 w-36 rounded-sm" data-testid="b2b-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte cat.</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterAvail} onValueChange={setFilterAvail}>
                  <SelectTrigger className="h-9 w-40 rounded-sm" data-testid="b2b-avail"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="pronto">Pronta consegna</SelectItem>
                    <SelectItem value="importazione">Solo in arrivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              <div className="bg-white border border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FAFAFA] border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Th>Foto</Th><Th>Articolo</Th><Th>Combinazione</Th>
                        <Th right>€/cad</Th><Th center>Pronto</Th><Th center>In arrivo</Th><Th center>Aggiungi</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">Nessun articolo disponibile</td></tr>
                      ) : filtered.map((it) => {
                        const inCart = cart[it.sku_variante] || 0;
                        const maxQty = it.stock_pronto + it.stock_importazione;
                        return (
                          <tr key={it.sku_variante} className="border-b border-border last:border-0 hover:bg-accent/30" data-testid={`b2b-row-${it.sku_variante}`}>
                            <td className="px-3 py-2">
                              {it.immagine ? <img src={it.immagine} alt="" className="w-10 h-10 rounded-sm object-cover" /> : <div className="w-10 h-10 bg-neutral-100 rounded-sm" />}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium leading-tight">{it.nome}</div>
                              <div className="text-[10px] font-mono text-muted-foreground">{it.sku_variante}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(it.combination || {}).map(([k, v]) => (
                                  <span key={k} className="text-[10px] px-1.5 py-0.5 bg-neutral-100 rounded-sm">{v}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-medium">€ {it.prezzo_riv}</td>
                            <td className="px-3 py-2 text-center">
                              {it.stock_pronto > 0 ? <span className="text-emerald-700 font-medium">{it.stock_pronto}</span> : <span className="text-neutral-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {it.stock_importazione > 0 ? <span className="text-amber-700">{it.stock_importazione}</span> : <span className="text-neutral-400">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => addQty(it.sku_variante, maxQty, -1)} disabled={inCart === 0}
                                  data-testid={`b2b-minus-${it.sku_variante}`}
                                  className="w-6 h-6 rounded-sm border border-border hover:bg-accent disabled:opacity-30"><Minus className="w-3 h-3 mx-auto" /></button>
                                <span className="w-8 text-center font-medium text-sm" data-testid={`b2b-qty-${it.sku_variante}`}>{inCart}</span>
                                <button onClick={() => addQty(it.sku_variante, maxQty, 1)} disabled={inCart >= maxQty}
                                  data-testid={`b2b-plus-${it.sku_variante}`}
                                  className="w-6 h-6 rounded-sm border border-border hover:bg-accent disabled:opacity-30"><Plus className="w-3 h-3 mx-auto" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Cart sidebar */}
            <div className="bg-white border border-border rounded-sm p-4 sticky top-4 self-start" data-testid="b2b-cart">
              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
                <FileText className="w-4 h-4 text-brand" />
                <h3 className="font-heading font-bold text-base">Nuovo PO</h3>
                <span className="ml-auto text-xs text-muted-foreground">{Object.keys(cart).length} righe</span>
              </div>
              <div className="space-y-1.5 max-h-80 overflow-auto mb-3">
                {Object.entries(cart).length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-6">Aggiungi articoli dal catalogo</div>
                ) : Object.entries(cart).map(([sku, qty]) => {
                  const it = items.find((i) => i.sku_variante === sku);
                  if (!it) return null;
                  return (
                    <div key={sku} className="flex items-center gap-2 text-xs p-2 bg-neutral-50 rounded-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{it.nome}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">{sku}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-medium">{qty} × € {it.prezzo_riv}</div>
                        <div className="text-[10px] text-muted-foreground">€ {(qty * it.prezzo_riv).toFixed(2)}</div>
                      </div>
                      <button onClick={() => setCart((c) => { const cc = { ...c }; delete cc[sku]; return cc; })}
                        className="text-red-500 hover:bg-red-50 rounded-sm w-5 h-5" data-testid={`b2b-remove-${sku}`}><X className="w-3 h-3 mx-auto" /></button>
                    </div>
                  );
                })}
              </div>
              {Object.keys(cart).length > 0 && (
                <>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note operative (opzionale)..." rows={2}
                    className="w-full text-xs px-2 py-1.5 rounded-sm border border-border mb-3" data-testid="b2b-note" />
                  <div className="flex items-baseline justify-between mb-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">Totale PO</span>
                    <span className="font-heading font-bold text-xl">€ {totaleCarrello.toFixed(2)}</span>
                  </div>
                  <button onClick={submitPO} disabled={submitting} data-testid="b2b-submit"
                    className="w-full h-10 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Send className="w-4 h-4" /> {submitting ? "Invio..." : "Invia PO a Casa Madre"}
                  </button>
                  <p className="text-[10px] text-muted-foreground mt-2 text-center">Lo stock CM viene riservato all'invio. Pagamento a saldo come da accordo B2B.</p>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-2">
            {orders.length === 0 ? (
              <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm">Nessun PO inviato</div>
            ) : orders.map((o) => (
              <div key={o.id} className="bg-white border border-border rounded-sm p-3" data-testid={`po-row-${o.numero_ordine}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">{o.numero_ordine}</span>
                    <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={o.status} />
                    <span className="font-medium text-sm">€ {o.totale}</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">{o.items?.length || 0} articoli</div>
                {o.note && <div className="text-xs italic text-muted-foreground mt-1">"{o.note}"</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const Th = ({ children, center, right }) => (
  <th className={`px-3 py-2 ${center ? "text-center" : right ? "text-right" : "text-left"}`}>{children}</th>
);

const StatusPill = ({ status }) => {
  const map = {
    pagato_da_rivenditore: { label: "Inviato a CM", color: "bg-blue-100 text-blue-800", icon: Send },
    confermato: { label: "Confermato", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
    in_produzione: { label: "In produzione", color: "bg-amber-100 text-amber-800", icon: Clock },
    spedito: { label: "Spedito", color: "bg-violet-100 text-violet-800", icon: Package },
    consegnato: { label: "Consegnato", color: "bg-emerald-200 text-emerald-900", icon: CheckCircle2 },
    annullato: { label: "Annullato", color: "bg-red-100 text-red-800", icon: X },
  };
  const c = map[status] || { label: status, color: "bg-neutral-100 text-neutral-700", icon: Clock };
  const Icon = c.icon;
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${c.color}`}><Icon className="w-3 h-3" />{c.label}</span>;
};
